import { useEffect, useMemo, useState, useCallback } from 'react';

/**
 * useTourState — generic localStorage persistence for guided tours.
 *
 * MODULE-AGNOSTIC: no knowledge of any specific module. Each module's tour
 * controller calls this with its own sectionKey and phase names.
 *
 * Keys (namespaced per section):
 *   tour_<sectionKey>_visits          — int, incremented once per tab session
 *   tour_<sectionKey>_<phase>_done    — '1' when that phase completed/skipped
 *
 * The visit counter only increments when `countVisit` is true (e.g. desktop
 * only), guarded by a sessionStorage flag so StrictMode double-mounts and
 * in-app navigation count one visit per tab session.
 */
export default function useTourState({ sectionKey, phases = [], maxAutoVisits = 2, countVisit = true }) {
  const visitsKey = `tour_${sectionKey}_visits`;
  const sessionGuardKey = `tour_${sectionKey}_visit_counted`;
  const phaseKey = useCallback(
    (phase) => `tour_${sectionKey}_${phase}_done`,
    [sectionKey]
  );

  const readVisits = () => {
    try {
      return parseInt(localStorage.getItem(visitsKey), 10) || 0;
    } catch (e) {
      return 0;
    }
  };

  const [visits, setVisits] = useState(readVisits);
  // Done-flags mirrored in state so consumers re-render on markPhaseDone.
  const [doneFlags, setDoneFlags] = useState(() => {
    const flags = {};
    for (const p of phases) {
      try {
        flags[p] = localStorage.getItem(`tour_${sectionKey}_${p}_done`) === '1';
      } catch (e) {
        flags[p] = false;
      }
    }
    return flags;
  });

  // Count the visit once per tab session
  useEffect(() => {
    if (!countVisit) return;
    try {
      if (sessionStorage.getItem(sessionGuardKey)) return;
      sessionStorage.setItem(sessionGuardKey, '1');
      const next = readVisits() + 1;
      localStorage.setItem(visitsKey, String(next));
      setVisits(next);
    } catch (e) {
      /* storage unavailable — tour auto-start simply won't trigger */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countVisit, sectionKey]);

  const isPhaseDone = useCallback((phase) => !!doneFlags[phase], [doneFlags]);

  const markPhaseDone = useCallback((phase) => {
    try {
      localStorage.setItem(phaseKey(phase), '1');
    } catch (e) { /* noop */ }
    setDoneFlags((prev) => ({ ...prev, [phase]: true }));
  }, [phaseKey]);

  const markAllDone = useCallback(() => {
    setDoneFlags((prev) => {
      const next = { ...prev };
      for (const p of phases) {
        try {
          localStorage.setItem(phaseKey(p), '1');
        } catch (e) { /* noop */ }
        next[p] = true;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseKey, phases.join('|')]);

  const shouldAutoStart = useCallback(
    (phase) => countVisit && visits > 0 && visits <= maxAutoVisits && !doneFlags[phase],
    [countVisit, visits, maxAutoVisits, doneFlags]
  );

  return useMemo(
    () => ({ visits, isPhaseDone, markPhaseDone, markAllDone, shouldAutoStart }),
    [visits, isPhaseDone, markPhaseDone, markAllDone, shouldAutoStart]
  );
}
